import moment from 'moment';

// ===== TYPE DEFINITIONS =====

export type PurchaseInvoiceStatus = 'pending' | 'processing' | 'processed' | 'failed';

export interface PurchaseInvoiceRow {
  id: string;
  purchaseDate: string;
  createdDate: string;
  vendorName: string;
  totalAmount: number;
  status: PurchaseInvoiceStatus;
  isFinalized?: boolean;
}

// ===== STATUS MAPPING =====

export const API_STATUS_MAP: Record<string, PurchaseInvoiceStatus> = {
  'pending': 'pending',
  'processing': 'processing',
  'processed': 'processed',
  'failed': 'failed',
  'partial': 'processing',
  'completed': 'processed',
};

const mapApiPurchaseStatus = (apiStatus: string): PurchaseInvoiceStatus => {
  return API_STATUS_MAP[apiStatus.toLowerCase()] || 'pending';
};

/**
 * Purchase Invoice Model - Encapsulates purchase invoice data and provides clean interface for UI components
 * This model handles purchase invoice data transformation, validation, and calculated properties
 */
class PurchaseInvoiceModel {
  private _serverData: any;

  constructor(serverData: any = null) {
    this._serverData = serverData;
  }

  // ===== SERVER DATA PROPERTIES (Only fields actually used in UI) =====

  get id() { return this._serverData?.id; }
  get invoice_date() { return this._serverData?.invoice_date; }
  get created_at() { return this._serverData?.created_at; }
  get vendor_name() { return this._serverData?.vendor_name; }
  get grand_total() { return this._serverData?.grand_total; }
  get status() { return this._serverData?.status; }
  get is_finalized() { return !!this._serverData?.is_finalized; }

  // ===== CALCULATED PROPERTIES =====

  get isPending() {
    const mappedStatus = mapApiPurchaseStatus(this.status || '');
    return mappedStatus === 'pending';
  }

  get isProcessing() {
    const mappedStatus = mapApiPurchaseStatus(this.status || '');
    return mappedStatus === 'processing';
  }

  get isProcessed() {
    const mappedStatus = mapApiPurchaseStatus(this.status || '');
    return mappedStatus === 'processed';
  }

  get isFailed() {
    const mappedStatus = mapApiPurchaseStatus(this.status || '');
    return mappedStatus === 'failed';
  }

  // ===== FORMATTED PROPERTIES =====

  get formattedPurchaseDate() {
    if (!this.invoice_date) return '-';
    return moment(this.invoice_date).format('MM/DD/YYYY');
  }

  get formattedCreatedDate() {
    if (!this.created_at) return '-';
    return moment(this.created_at).format('MM/DD/YYYY hh:mm A');
  }

  get formattedTotalAmount() {
    if (!this.grand_total || this.grand_total === 0) {
      return '-';
    }
    return `$${this.grand_total.toFixed(2)}`;
  }

  get mappedStatus(): PurchaseInvoiceStatus {
    return mapApiPurchaseStatus(this.status || '');
  }

  // ===== UTILITY METHODS =====

  // Get raw server data (for backward compatibility)
  getRawData() {
    return this._serverData;
  }

  // Convert to PurchaseInvoiceRow format (for table display)
  toRowFormat(): PurchaseInvoiceRow {
    return {
      id: this.id || '',
      purchaseDate: this.formattedPurchaseDate,
      createdDate: this.formattedCreatedDate,
      vendorName: this.vendor_name || '-',
      totalAmount: this.grand_total || 0,
      status: this.mappedStatus,
      isFinalized: this.is_finalized,
    };
  }

  // ===== STATIC METHODS =====

  static fromServerData(serverData: any) {
    return new PurchaseInvoiceModel(serverData);
  }
}

/**
 * Purchase Invoices List Model - Manages a collection of purchase invoices
 * Provides methods for accessing invoices
 */
class PurchaseInvoicesListModel {
  private _invoices: PurchaseInvoiceModel[];
  private _total: number;

  constructor(serverData: any = null) {
    if (serverData) {
      // Handle API response structure: { purchases: [...], total: number }
      const invoices = serverData.purchases && Array.isArray(serverData.purchases)
        ? serverData.purchases.map((invoice: any) => PurchaseInvoiceModel.fromServerData(invoice))
        : [];
      
      this._invoices = invoices;
      this._total = serverData.total ?? 0;
    } else {
      this._invoices = [];
      this._total = 0;
    }
  }

  // ===== BASIC PROPERTIES =====

  get invoices() { return this._invoices; }
  get total() { return this._total; }

  // ===== UTILITY METHODS =====

  // Convert invoices to row format (for table display)
  toRowFormat() {
    return this._invoices.map(invoice => invoice.toRowFormat());
  }

  // ===== STATIC METHODS =====

  static fromServerData(serverData: any) {
    return new PurchaseInvoicesListModel(serverData);
  }

  static createEmpty() {
    return new PurchaseInvoicesListModel();
  }
}

export default PurchaseInvoiceModel;
export { PurchaseInvoicesListModel };
